import { useMemo } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { Account } from 'features/settings/components/Account'
import { Logo } from 'features/settings/components/Logo'
import { CompanyDomainsTable } from 'features/settings/components/CompanyDomainsTable'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

type CompanyTab = 'account' | 'logo' | 'domains'

export function CompanySettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { user, isAllowed } = useAuth()
  const featureFlags = useFeatureFlagService()
  const isReadOnly = !isAllowed('Admin')

  const tabs = useMemo(() => {
    const allTabs: { key: CompanyTab; title: string }[] = []

    if (user?.is_billing_admin) {
      allTabs.push({ key: 'account', title: 'Account' })
    }

    allTabs.push({ key: 'logo', title: 'Logo' })

    if (featureFlags.enableCompanyEmailExtensionSettings) {
      allTabs.push({ key: 'domains', title: 'Domains' })
    }

    return allTabs
  }, [user?.is_billing_admin, featureFlags.enableCompanyEmailExtensionSettings])

  const defaultTab: CompanyTab = tabs[0]?.key ?? 'account'
  const tab = (params.tab as CompanyTab) ?? defaultTab

  if (params.tab && !tabs.some((t) => t.key === params.tab)) {
    return <Navigate to={`/settings/company/${defaultTab}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-[#fefefe]">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<CompanyTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/company/${value}`)}
          idPrefix="company-settings"
          aria-label="Company settings tabs"
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer className="bg-[#fefefe]" hide={tab !== 'account'}>
        <Account disabled={isReadOnly} />
      </FBPageTableContainer>
      <FBPageTableContainer className="bg-[#fefefe]" hide={tab !== 'logo'}>
        <Logo disabled={isReadOnly} />
      </FBPageTableContainer>
      <FBPageTableContainer className="bg-[#fefefe]" hide={tab !== 'domains'}>
        <CompanyDomainsTable disabled={isReadOnly} />
      </FBPageTableContainer>
    </div>
  )
}
